/**
 * Deep-reference presentation helpers for the create/update reference picker.
 *
 * A CRUD reference token looks like `<Entity>$a$b%RefEntity_for_CRUD` — the `$`-path encodes nesting,
 * `%RefEntity` is the referenced entity, `_for_CRUD` is the picker suffix. These helpers turn that into a
 * human breadcrumb and enforce the orphan rule: a nested reference is only meaningful when its parent is
 * being built inline (a *linked* parent already carries its own children), so a nested option is disabled
 * unless its parent selection is present AND in `build` mode. See
 * docs/superpowers/specs/2026-09-08-crud-reference-fields-redesign.md §D.
 */

/** The `$`-path segments of a ref token, minus the `%RefEntity` and `_for_CRUD` suffixes. */
function refPath(token: string): string[] {
  return (token || '').replace(/_for_CRUD$/, '').split('%')[0].split('$').filter(Boolean);
}

/** Turn a CRUD reference token (`<Entity>$a$b%RefEntity_for_CRUD`) into `Entity › a › b`. */
export function referenceCrumbLabel(token: string, entityName: string): string {
  const parts = refPath(token); // [Order, customer, address]
  if (parts.length && entityName && parts[0] !== entityName) parts[0] = entityName;
  return parts.join(' › ');
}

/**
 * True when `token` is a NESTED reference whose parent selection is absent from the chain, or present but
 * not in `build` mode. Top-level references (`[Entity, field]`) are never orphaned.
 */
export function isReferenceOrphaned(
  token: string,
  chain: { token: string; mode: 'link' | 'build' }[]
): boolean {
  const path = refPath(token); // [Order, customer, address]
  if (path.length <= 2) return false; // [Entity, field] = top-level
  const parentPrefix = path.slice(0, -1).join('$') + '%'; // Order$customer%
  const parent = (chain || []).find((c) => c.token.startsWith(parentPrefix));
  return !parent || parent.mode !== 'build';
}
